/**
 * `useShellStore` — the shell's **ephemeral** UI state (spec M0-F4 §6).
 *
 * Anything here is allowed to disappear on reload. Everything that must survive one lives in
 * `preferences.ts` and is written through `usePreference`; the two are deliberately separate so
 * it is never ambiguous which of them owns a given flag.
 */
import { create } from 'zustand';

/** Which pane mode the rail has selected (F4 §7.3). */
export type RailSlot = 'workspace' | 'favorites' | 'more';

/** The overlays the shell itself opens. URL-driven surfaces are NOT in here (§7.1 rule b). */
export type ShellOverlay = 'notifications' | 'search' | 'shortcuts' | 'invite' | null;

export interface ShellState {
  railSlot: RailSlot;
  /** The pane's in-place filter (S6). Not persisted: it is a transient search, not a setting. */
  treeFilter: string;
  treeFilterOpen: boolean;
  /** Set while the pane's right border is being dragged, so the app can suppress text selection. */
  resizing: boolean;
  /** Below 1024 px the pane is an overlay with a scrim (§7.10). */
  paneOverlayOpen: boolean;
  overlay: ShellOverlay;
  /** The socket-down banner (N3). */
  reconnecting: boolean;

  setRailSlot: (slot: RailSlot) => void;
  setTreeFilter: (value: string) => void;
  setTreeFilterOpen: (open: boolean) => void;
  setResizing: (resizing: boolean) => void;
  setPaneOverlayOpen: (open: boolean) => void;
  openOverlay: (overlay: NonNullable<ShellOverlay>) => void;
  toggleOverlay: (overlay: NonNullable<ShellOverlay>) => void;
  closeOverlay: () => void;
  setReconnecting: (value: boolean) => void;
}

const INITIAL = {
  railSlot: 'workspace' as RailSlot,
  treeFilter: '',
  treeFilterOpen: false,
  resizing: false,
  paneOverlayOpen: false,
  overlay: null as ShellOverlay,
  reconnecting: false,
};

export const useShellStore = create<ShellState>((set) => ({
  railSlot: 'workspace',
  treeFilter: '',
  treeFilterOpen: false,
  resizing: false,
  paneOverlayOpen: false,
  overlay: null,
  reconnecting: false,

  setRailSlot: (railSlot) => set({ railSlot }),
  setTreeFilter: (treeFilter) => set({ treeFilter }),
  setTreeFilterOpen: (treeFilterOpen) =>
    set(treeFilterOpen ? { treeFilterOpen } : { treeFilterOpen, treeFilter: '' }),
  setResizing: (resizing) => set({ resizing }),
  setPaneOverlayOpen: (paneOverlayOpen) => set({ paneOverlayOpen }),
  openOverlay: (overlay) => set({ overlay }),
  toggleOverlay: (overlay) => set((state) => ({ overlay: state.overlay === overlay ? null : overlay })),
  closeOverlay: () => set({ overlay: null }),
  setReconnecting: (reconnecting) => set({ reconnecting }),
}));

/** Test-only: the store is a module singleton, so a suite has to be able to reset it. */
export function __resetShellStore() {
  useShellStore.setState(INITIAL);
}
